import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, Lock, User } from 'lucide-react';

import { authService } from '../services/authService';

const schema = z.object({
  fullName: z.string().min(2, 'Enter your full name'),
  email: z.string().email('Enter a valid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
});

type FormData = z.infer<typeof schema>;

interface SignUpFormProps {
  onSuccess?: () => void;
}

export function SignUpForm({ onSuccess }: SignUpFormProps) {
  const [serverMessage, setServerMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    setServerMessage('');
    const result = await authService.signUp(data.email, data.password, data.fullName);
    setIsLoading(false);
    setServerMessage(result.message);
    if (result.ok) {
      setIsSuccess(true);
      onSuccess?.();
    }
  };

  if (isSuccess) {
    return (
      <div className="text-center py-4">
        <p className="text-h4 text-midnight mb-2">Check your email</p>
        <p className="text-small text-ink-500">{serverMessage}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
      <div className="flex flex-col gap-1">
        <label htmlFor="fullName" className="text-small font-medium text-ink-700">
          Full name
        </label>
        <div className="relative">
          <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-300 pointer-events-none" />
          <input
            id="fullName"
            type="text"
            autoComplete="name"
            placeholder="Jane Smith"
            {...register('fullName')}
            className="w-full h-10 pl-9 pr-3 border border-ink-100 rounded-md text-body text-ink-900 placeholder:text-ink-300 focus:border-violet-400 focus:ring-1 focus:ring-violet-400 outline-none transition-colors"
          />
        </div>
        {errors.fullName && (
          <p className="text-caption text-danger-400">{errors.fullName.message}</p>
        )}
      </div>

      <div className="flex flex-col gap-1">
        <label htmlFor="su-email" className="text-small font-medium text-ink-700">
          Email address
        </label>
        <div className="relative">
          <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-300 pointer-events-none" />
          <input
            id="su-email"
            type="email"
            autoComplete="email"
            placeholder="you@example.co.uk"
            {...register('email')}
            className="w-full h-10 pl-9 pr-3 border border-ink-100 rounded-md text-body text-ink-900 placeholder:text-ink-300 focus:border-violet-400 focus:ring-1 focus:ring-violet-400 outline-none transition-colors"
          />
        </div>
        {errors.email && (
          <p className="text-caption text-danger-400">{errors.email.message}</p>
        )}
      </div>

      <div className="flex flex-col gap-1">
        <label htmlFor="su-password" className="text-small font-medium text-ink-700">
          Password
        </label>
        <div className="relative">
          <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-300 pointer-events-none" />
          <input
            id="su-password"
            type="password"
            autoComplete="new-password"
            placeholder="At least 8 characters"
            {...register('password')}
            className="w-full h-10 pl-9 pr-3 border border-ink-100 rounded-md text-body text-ink-900 placeholder:text-ink-300 focus:border-violet-400 focus:ring-1 focus:ring-violet-400 outline-none transition-colors"
          />
        </div>
        {errors.password && (
          <p className="text-caption text-danger-400">{errors.password.message}</p>
        )}
      </div>

      {serverMessage && !isSuccess && (
        <p className="text-small text-danger-400 bg-danger-bg px-3 py-2 rounded-sm">
          {serverMessage}
        </p>
      )}

      <button
        type="submit"
        disabled={isLoading}
        className="w-full h-11 bg-midnight text-paper rounded-md font-medium text-small hover:opacity-90 active:opacity-80 disabled:opacity-50 transition-opacity"
      >
        {isLoading ? 'Creating account…' : 'Create account'}
      </button>
    </form>
  );
}
